import { Moment } from 'moment';
import React, { useEffect, useRef } from 'react';

import { $ } from '../../base/bootstrap';
import useGlobalState from '../GlobalStateContext';
import PartitionMetric from '../elements/metrics/PartitionMetric';
import PlainMetric from '../elements/metrics/PlainMetric';
import TrendMetric from '../elements/metrics/TrendMetric';
import ValueMetric from '../elements/metrics/ValueMetric';

import { PermissionType } from './Permissions';

export enum TYPE_METRICS {
    TREND_METRIC = 'TrendMetric',
    VALUE_METRIC = 'ValueMetric',
    PARTITION_METRIC = 'PartitionMetric',
    PLAIN_METRIC = 'PlainMetric',
}

export type TimeRangeName =
    | 'today'
    | 'yesterday'
    | 'this-week'
    | 'this-month'
    | 'last-week'
    | 'last-month'
    | 'all-time'
    | 'date-range';

export type MetricsTimeRange = {
    range?: TimeRangeName;
    startDate?: Moment;
    endDate?: Moment;
};

type ShowMetricsProps = {
    url: string;
    path: string;
    label: string;
    typeMetrics: TYPE_METRICS;
    timeRange?: MetricsTimeRange;
    tooltip?: string;
};

export default function ShowMetrics({
    url,
    path,
    label,
    typeMetrics,
    timeRange = {},
    tooltip = null,
}: ShowMetricsProps) {
    const { loggedAdmin } = useGlobalState();
    const permissionName = `view metrics ${path.replace(/_/g, ' ').replace(/\//g, ' ')}`;

    if (!loggedAdmin.hasPermission(permissionName as PermissionType)) {
        return null;
    }

    const fullUrl = `${url}/${path}`;
    const { range = null, startDate = null, endDate = null } = timeRange;

    switch (typeMetrics) {
        case TYPE_METRICS.TREND_METRIC:
            return (
                <MetricsWrapper tooltip={tooltip}>
                    <TrendMetric
                        url={fullUrl}
                        label={label}
                        range={range}
                        startDate={startDate}
                        endDate={endDate}
                        elementId={`trend-chart-${label.replace(/ /g, '_')}`}
                    />
                </MetricsWrapper>
            );

        case TYPE_METRICS.PARTITION_METRIC:
            return (
                <MetricsWrapper tooltip={tooltip}>
                    <PartitionMetric url={fullUrl} label={label} startDate={startDate} endDate={endDate} />
                </MetricsWrapper>
            );

        case TYPE_METRICS.PLAIN_METRIC:
            return (
                <MetricsWrapper tooltip={tooltip}>
                    <PlainMetric url={fullUrl} label={label} />
                </MetricsWrapper>
            );

        case TYPE_METRICS.VALUE_METRIC:
        default:
            return (
                <MetricsWrapper tooltip={tooltip}>
                    <ValueMetric url={fullUrl} label={label} range={range} startDate={startDate} endDate={endDate} />
                </MetricsWrapper>
            );
    }
}

function MetricsWrapper({ tooltip, children }: { tooltip: string; children: React.ReactNode }) {
    const element = useRef();

    useEffect(() => {
        $(element.current).tooltip();
    }, []);

    return (
        <div className="col-lg-4 col-md-6 col-sm-12">
            {children}
            {tooltip?.length ? (
                <i
                    ref={element}
                    className="metrics-tile-tooltip fas fa-info-circle"
                    data-toggle="tooltip"
                    data-placement="top"
                    data-html="true"
                    title={tooltip}
                />
            ) : null}
        </div>
    );
}
